import React, { useEffect, useState } from "react";
import { filterPrice, rememberField, setRememberField } from "../utils/Utils";
import { showError } from "./Message";

const PriceField = React.forwardRef((props, ref) => {
    const {
        remember = "",
        value = 0, // Value in cents
        width = 0,
        prefill = false,
        hide_zero = false,
        className = "",
        secondary = false,
        warning = false,
        black = false,
        style = {},
        right = false,
        medium,
        edit = false,
        placeholder = "",
        onEnter = null,
        onPageUp = null,
        setHelp = () => { },
        onChange = () => { },
        onClick = () => { },
        onFocus = () => { },
        onBlur = () => { },
        controlled = false,
        render = false,
        autoselect = false,
        doubleclick = false,
        minLength = 0,
        maxLength = 0,
        cell = false,
        border = false,
        double = false,
        autofocus = false,
        grow = false,
        shrink = true,
        restrict = false,
        restrictMsg = "",
        opened = false,
        tabMax = false,
        tabMin = false,
        help = false,
        onFill = false,
        dataId = "",
    } = props;

    const [isFocused, setIsFocused] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [inputValue, setInputValue] = useState(formatInputValue(value));

    // === Helpers ===


    function formatDisplayValue(cents) {
        if (hide_zero && Number(cents) === 0) return "";
        if (cents === null || cents === "" || cents === undefined) return "";
        return (cents / 100).toFixed(2);
    }

    function formatFocusValue(cents) {
        if (Number(cents) === 0 && prefill !== false) {
            return (prefill / 100).toFixed(2);
        }

        return formatDisplayValue(cents);
    }

    useEffect(() => {
        if (controlled && !isFocused) {
            setInputValue(formatDisplayValue(value));
        }
    }, [controlled, value, isFocused, edit]);

    function formatInputValue(cents, usePrefill = true) {

        const isEmpty =
            cents === null ||
            cents === "" ||
            cents === undefined ||
            Number(cents) === 0;

        // show prefill for zero/empty values
        if (isEmpty && usePrefill && prefill !== false) {
            return (prefill / 100).toFixed(2);
        }

        // otherwise optionally hide zero
        if (hide_zero && Number(cents) === 0) {
            return "";
        }

        if (cents === null || cents === "" || cents === undefined) {
            return "";
        }

        return (cents / 100).toFixed(2);
    }

    function parsePrice(val) {
        if (val === "" || val === "-") return 0;
        const num = parseFloat(val);
        if (isNaN(num)) return 0;
        return Math.round(num * 100);
    }

  
    useEffect(() => {
        if (!edit) {
            setIsFocused(false);
        }
    }, [edit, value]);

    // === Change handler ===
    const change = (_v) => {
        _v = typeof _v === "string" ? _v : "";

        _v = filterPrice(_v) || "";
        _v = _v.toString();

        setInputValue(_v);

        if (_v === "-") {
            setIsValid(true);
            onChange(0);
            return;
        }

        const decimalMatch = _v.match(/^-?\d*\.?\d{0,2}$/);
        if (!decimalMatch && _v !== "") {
            setIsValid(false);
            if (restrict && restrictMsg) showError({ error: restrictMsg });
            return;
        }

        setIsValid(true);

        if (maxLength && _v.length > maxLength) {
            _v = _v.slice(0, maxLength);
            setInputValue(_v);
        }

        const cents = parsePrice(_v);

        if (onFill && _v.length === maxLength) onFill(cents);
        if (tabMax !== false && _v.length === maxLength) tabMax();
        if (tabMin !== false && _v.length === 0) tabMin();

        onChange(cents);
    };


    const handleFocus = (e) => {
        setIsFocused(true);
        setInputValue(formatFocusValue(value));

        setTimeout(() => {
            if (autoselect) e.target.select();
        }, 1);

        if (help !== false) setHelp(help);
        onFocus();
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        setIsValid(true);

        const cents = parsePrice(e.target.value);
        setInputValue(formatDisplayValue(cents));

        onBlur(cents);
    };

    const handleDoubleClick = (e) => {
        if (typeof doubleclick === "function") doubleclick(e);
    };

    // === CSS classes ===
    let _class = edit ? "ui-input edit" : "ui-input";
    if (cell) {
        _class = edit ? "ui-cell-edit" : "ui-cell";
        if (border) _class += " border";
        if (double) _class += " double";
        if (width) style.minWidth = width + "px";
    }
    if (secondary) _class += " secondary";
    if (warning) _class += " warning";
    if (className) _class += ` ${className}`;
    if (right) _class += " ra";
    if (width && !cell) style.width = `${width}px`;
    if (medium) _class += " medium";
    if (black) _class += " black";
    if (!isValid && edit) _class += " error";
    if (grow) {
        style.flexGrow = 1;
        style.width = width;
    }
    if (!shrink) style.flexShrink = 0;
    if (opened) _class += " select";

    // === Render ===
    if (edit) {
        return (
            <input
                data-id={dataId}
                type="text"
                {...(minLength && { minLength })}
                {...(maxLength && { maxLength })}
                autoFocus={autofocus}
                onDoubleClick={doubleclick ? handleDoubleClick : undefined}
                spellCheck={false}
                onKeyDown={(e) => {

                    if (e.key === "F5") {
                        e.stopPropagation();
                        e.preventDefault();
                        if (remember) {
                            const r = rememberField(remember);
                            if (r || r === 0) {
                                change(r);
                            }
                        }
                    }

                    if (e.key === "Enter") {
                         if (remember) {
                                setRememberField(remember, e.target.value)
                            }
                        if (onEnter) {
                            if (e?.shiftKey) return;
                            onEnter(parsePrice(e.target.value), e);
                            e.target.value = "";
                        }

                    }
                    if (e.key === "PageUp" && onPageUp) {
                        onPageUp(parsePrice(e.target.value));
                        return; 
                    
                    }
                }}
                autoComplete="off"
                onChange={(e) => change(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                ref={ref}
                placeholder={placeholder}
                style={{ ...style }}
                className={_class}
                value={controlled ? inputValue : undefined}
                defaultValue={!controlled ? formatDisplayValue(value) : undefined}
            />
        );
    }

    return (
        <div data-id={dataId} onClick={() => onClick()} style={{ ...style }} className={_class}>
            {render !== false
                ? render
                : !value && placeholder
                    ? <em style={{ opacity: "0.5" }}>{placeholder}</em>
                    : formatDisplayValue(value)}
        </div>
    );
});

export default PriceField;
